<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import type { KeyedStringEnum } from "@/providers/Materials/LibraryEnumsProvider";

import { Material } from "@/types/Views/MaterialDatabase/Material";

import { useEnum } from "@/stores/project/project.enum.ts";
import { useFilters } from "@/stores/project/project.filters";
import { useCategoryTranslations } from "@/dataProviders/CategoryTranslations";

import format from "@/services/Utils/format";
import StagesTableModal from "@/components/Views/MaterialMapping/Library/Modal/StagesTableModal.vue";
import { openFileModal } from "@/components/Views/MaterialMapping/Modals/openFileModal";

import TagItem from "@/components/Reusables/Utils/TagItem.vue";
import LCIAChart from "@/components/Views/MaterialDatabase/InspectMaterial/LCIAChart.tsx";
import InspectMaterialTools from "@/features/InspectMaterialToolsNew";

import ConstructionEpd from "@/components/Views/MaterialMapping/Library/Modal/InspectConstruction/ConstructionEpd.vue";

import i18n from "@/services/i18n";
import Card from "@/components/Reusables/Card/Card.vue";
import { ArrowLeft, FileText, Table } from "@lucide/vue";
import { declaredUnitSymbol } from "@/utils/declaredUnitSymbol";
import { massUnitSymbol } from "@/utils/massUnitSymbol";

const filterStore = useFilters();
const { libraryFilters } = storeToRefs(filterStore);

const categoryTranslations = useCategoryTranslations();

const props = defineProps<{
  includedPhases?: Array<number>;
  material: Material;
  parentName?: string | null;
}>();

const emit = defineEmits<{
  (e: "onClick", value: unknown): void;
  // The caller owns what "back" means: popping a stacked modal, or swapping a view back inside one.
  // This component used to call modals.closeTop() itself, which only worked for the modal case.
  (e: "back"): void;
}>();

const onClick = (value: unknown) => {
  emit("onClick", value);
};

const enumStore = useEnum();
const { indicatorTypeNames, specificationForms, productTypes, indicatorUnits, massUnits, declaredUnits } = storeToRefs(enumStore);

const indicatorTableVisible = ref<boolean>(false);

const datasheet = [
  {
    label: "Datasource",
    content() {
      if (!props.material) return "";
      return (props.material.datasourceName || props.material.datasourceNames?.join(", ")) ?? "-";
    },
    link() {
      return (props.material?.epdInfo?.link?.replace("?alttemplate=EPDpopup&lang=da-dk", "") ?? "") || "";
    },
    width: 4,
  },
  {
    label: "EPD Type",
    content() {
      if (!props.material) return "";
      let name = "";
      if (props.material.isCustom) {
        name += "Custom ";
      }
      if (props.material.epdInfo && productTypes.value) {
        const type = libraryFilters.value.material.types?.find(
          (type) => type.code == String(props.material.epdInfo!.epdProductIndustryType)
        );

        name += `${type?.DisplayName} EPD`;
      }
      return name;
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "EPD Standard",
    content() {
      if (!props.material) return "";
      if (props.material.epdInfo && specificationForms.value) {
        const displayName = specificationForms.value[props.material.epdInfo.epdSpecificationForm]?.DisplayName;
        if (!displayName) return "-";
        return (
          displayName.substring(0, displayName.length - 2) +
          "+" +
          displayName.substring(displayName.length - 2, displayName.length)
        );
      }

      return "-";
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Declaration Number",
    content() {
      if (!props.material) return "";
      if (props.material.epdInfo) {
        return props.material.epdInfo.declarationNumber;
      }

      return "-";
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Weight per declared unit",
    content() {
      if (!props.material) return "";
      // If massUnit is 10 (Other) we don't show the mass
      if (props.material?.declaredUnit?.massUnit === 10) return "-";

      if (!massUnits.value) return;

      // Show the ratio symbol ("kg/m²"), not the spelled-out DisplayName.
      let massUnit = massUnitSymbol(massUnits.value[props.material.declaredUnit.massUnit]);

      if (!massUnit && props.material.declaredUnit.massUnitType) {
        massUnit = massUnitSymbol(massUnits.value[props.material.declaredUnit.massUnitType]);
      }

      const value =
        (props.material.declaredUnit?.mass !== undefined
          ? props.material.declaredUnit?.mass
          : props.material.declaredUnit?.massValue) ?? "";

      // A bare unit with no number in front of it ("kg/m²") tells the reader nothing, so report it
      // the same way the other fields report missing data (RTLCA-5191).
      if (value === "") return "-";

      return `${value} ${massUnit}`;
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Declared Unit",
    content() {
      if (!props.material) return "";

      // Show the unit symbol ("m²"), not the spelled-out DisplayName ("Square meters").
      let declaredUnit = declaredUnitSymbol(declaredUnits.value[props.material.declaredUnit.declaredUnit]);

      if (!declaredUnit && props.material.declaredUnit.epdDeclaredUnitType) {
        declaredUnit = declaredUnitSymbol(declaredUnits.value[props.material.declaredUnit.epdDeclaredUnitType]);
      }

      return `${props.material.declaredUnit?.declaredValue} ${declaredUnit}`;
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Issued",
    content() {
      if (!props.material) return "";
      return props.material.epdInfo ? format.date(props.material.epdInfo.publicationDate) : "-";
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Valid to",
    content() {
      if (!props.material) return "";
      return props.material.epdInfo ? format.date(props.material.epdInfo.validTo) : "-";
    },
    link() {
      return "";
    },
    width: 2,
  },
  {
    label: "Lifespan",
    content() {
      if (!props.material) return "";
      return props.material.expectedLifespan ? props.material.expectedLifespan : "Not Assigned";
    },
    link() {
      return "";
    },
    width: 2,
  },
];

// A construction is a recipe made of other EPDs rather than an EPD in its own right, so the
// EPD-specific fields have nothing to report and every one of them rendered as "-" (RTLCA-5191).
// Lifespan goes too: the lifespans that matter belong to the layers, listed further down.
const CONSTRUCTION_HIDDEN_FIELDS = [
  "Datasource",
  "EPD Type",
  "EPD Standard",
  "Declaration Number",
  "Issued",
  "Valid to",
  "Lifespan",
];

// Covers both the "-" the fields above return and the enums-not-loaded-yet case, where content()
// returns undefined for a moment.
const hasNothingToShow = (content: unknown) => {
  const text = String(content ?? "").trim();
  return text === "" || text === "-" || text === "Not Assigned";
};

// constructionEpds as a fallback: it is the layer list rendered below the datasheet, so anything
// carrying one is a construction even on a path where the API leaves isConstruction unset.
const isConstruction = computed(() => !!props.material?.isConstruction || (props.material?.constructionEpds?.length ?? 0) > 0);

// Materials keep the full datasheet, including fields with no value: an EPD that is missing its
// issue date is worth showing as missing. For a construction those fields aren't missing data,
// they don't apply, so drop them instead of printing a grid of dashes.
const visibleDatasheet = computed(() => {
  if (!isConstruction.value) return datasheet;

  return datasheet.filter((field) => !CONSTRUCTION_HIDDEN_FIELDS.includes(field.label) && !hasNothingToShow(field.content()));
});

const handleViewFile = async () => {
  const fileEntityId = props.material.epdInfo?.fileEntityId;
  // The button that calls this only renders when the file id is set.
  if (!fileEntityId) return;
  await openFileModal(fileEntityId);
};

const toggleIndicatorTable = () => {
  indicatorTableVisible.value = !indicatorTableVisible.value;
};

const owner = computed(() => {
  if (!props.material) return "Unknown Owner";
  return props.material.declarationOwnerName === "Unknown" ? "Unknown Owner" : props.material.declarationOwnerName;
});

const lastModifiedAt = computed(() => {
  return format.date(props.material.lastModifiedAt ?? props.material.createdAt ?? "");
});

const translateName = computed(() => {
  const locale = i18n.global.locale.value;
  const translation = props.material.names?.[locale];

  return translation?.translation ?? props.material.name;
});

const translateDesc = computed(() => {
  const locale = i18n.global.locale.value;
  return props.material.descriptions?.[locale]?.translation ?? props.material.description;
});

const categoryLabel = (category?: string | null) =>
  category ? categoryTranslations.translate(category, [i18n.global.locale.value]).value : "";
</script>

<template>
  <Card
    as="article"
    variant="outlined"
    padding="none"
    class="text-type-default w-full overflow-hidden">
    <!-- Header band -->
    <div class="flex flex-col gap-3 bg-slate-100 p-4">
      <div class="flex items-start justify-between gap-2">
        <div class="flex min-w-0 items-center gap-2">
          <!-- In the header row rather than absolutely positioned over it (RTLCA-5184): as an overlay at
               top-2 left-2 it sat on top of the truncating title and at a different inset from the
               modal's own close button, so the two read as unrelated strays. Sized like CloseButton so
               back and close are a matched pair. Only renders when opened from a parent (modal use);
               the standalone Library detail page passes no parentName. -->
          <button
            v-if="parentName"
            type="button"
            :aria-label="`Back to ${parentName}`"
            :title="`Back to ${parentName}`"
            class="focus-ring interactive-active rounded-8 text-type-default hover:bg-app-blue-200/30 duration-fast ease-standard -ml-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center transition-colors"
            @click="emit('back')">
            <ArrowLeft
              class="icon-md"
              :stroke-width="1.5" />
          </button>

          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="t-sh4 truncate">{{ translateName }}</span>
            <span class="t-footnote text-type-muted truncate">
              {{ owner }}
              <template v-if="material.tenantName"> · {{ material.tenantName }}</template>
              <template v-if="material.lastModifiedAt || material.createdAt"> · Updated {{ lastModifiedAt }}</template>
            </span>
          </div>
        </div>
        <InspectMaterialTools
          v-if="material.id && material.tenantId"
          :material-name="material.name"
          :material="material"
          :tenant-id="material.tenantId"
          :material-id="material.id"
          :show-open-in-new-tab="false" />
      </div>

      <!-- Quick actions + tags -->
      <div class="flex flex-wrap items-center gap-3">
        <button
          v-if="material.epdInfo?.fileEntityId && material.epdInfo.fileEntityId !== '00000000-0000-0000-0000-000000000000'"
          type="button"
          class="focus-ring rounded-4 text-type-muted hover:text-type-default duration-fast flex cursor-pointer items-center gap-1.5 transition-colors"
          @click="handleViewFile">
          <FileText
            class="icon-sm"
            :stroke-width="1.5" />
          <span class="t-caption">Documentation</span>
        </button>

        <button
          v-if="indicatorTypeNames"
          type="button"
          class="focus-ring rounded-4 duration-fast flex cursor-pointer items-center gap-1.5 transition-colors"
          :class="indicatorTableVisible ? 'text-app-blue-600' : 'text-type-muted hover:text-type-default'"
          @click="toggleIndicatorTable">
          <Table
            class="icon-sm"
            :stroke-width="1.5" />
          <span class="t-caption">Emission data</span>
        </button>

        <div class="ml-auto flex flex-wrap gap-2">
          <TagItem
            v-if="material.category"
            color="label"
            :label="categoryLabel(material.category)" />
          <TagItem
            v-else-if="material.tags.length > 0"
            color="label"
            :label="material.tags[0]" />
        </div>
      </div>
    </div>

    <!-- Datasheet + description -->
    <div v-show="!indicatorTableVisible">
      <!-- Auto-fit grid: tiles reflow to the pane width (independent of viewport), so it stays readable when the sidebar is narrow -->
      <div
        v-if="visibleDatasheet.length"
        class="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2 p-4">
        <div
          v-for="item in visibleDatasheet"
          :key="item.label"
          class="rounded-8 flex flex-col gap-0.5 border border-slate-200 bg-slate-50 p-3">
          <span class="t-footnote-bold text-type-muted uppercase">{{ item.label }}</span>
          <a
            v-if="item.link()"
            v-tooltip.truncated="item.content()"
            :href="item.link() || ''"
            target="_blank"
            rel="noopener noreferrer"
            class="t-caption text-app-blue-600 hover:text-app-blue-700 truncate underline"
            >{{ item.content() }}</a
          >
          <span
            v-if="!item.link()"
            v-tooltip.truncated="item.content()"
            class="t-caption text-type-default truncate"
            >{{ item.content() }}</span
          >
        </div>
      </div>

      <div class="flex flex-col gap-4 p-4 pt-0">
        <template v-if="material.constructionEpds && material.constructionEpds.length > 0">
          <ConstructionEpd
            :material="material.constructionEpds"
            :construction-unit="material.declaredUnit.declaredUnit"
            @on-click="onClick" />
        </template>

        <div class="flex flex-wrap items-start gap-4">
          <!-- Description — grows to fill when narrow, sits beside the chart when wide; v-html may contain wide tables/images, so keep it scrollable -->
          <div
            class="scrollbar t-small text-type-default min-w-0 flex-1 basis-[240px] space-y-1.5 overflow-auto leading-relaxed"
            v-html="translateDesc" />

          <!-- Chart — fixed-width chart scrolls within narrow containers (e.g. the Library split pane) -->
          <div
            v-if="indicatorUnits && indicatorTypeNames"
            class="scrollbar max-w-full overflow-x-auto">
            <LCIAChart
              :material="material"
              :included-phases="includedPhases"
              :indicator-enums="indicatorUnits"
              :indicator-type-enums="indicatorTypeNames as unknown as KeyedStringEnum" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="indicatorTableVisible"
      class="p-4">
      <StagesTableModal
        :lcia-results="material.lciaResults"
        :indicator-type-names="indicatorTypeNames" />
    </div>
  </Card>
</template>
